'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Service from '@/service';
import Checkout from '@/components/organisms/checkout';
import { mapAddress } from '@/utils/mappers/map-address';
import useCart from '@/lib/hooks/useCart';
import useAccount from '@/lib/hooks/useAccount';
import toast from '@/components/atoms/toaster/helpers/toast';
import { Props } from './types';
import { TasticProps } from '../types';
import getCookieValue from '@/utils/cookie';
import { getCurrencyByLocale } from '@/utils/currency-helpers';
import { ShippingAddressUpdatePayload } from '@shared/types/cart/Checkout';
import { useCartConfig } from '@/providers/cart-config/CartConfigContext';

const countries = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
];

const auStates = [
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'NT', label: 'NT' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'VIC', label: 'VIC' },
  { value: 'WA', label: 'WA' },
];

const nzStates = [
  { value: 'NorthIsland', label: 'North Island' },
  { value: 'SouthIsland', label: 'South Island' },
];


const CheckoutTastic = ({ data }: TasticProps<Props>) => {
  const router = useRouter();

  const [details, setDetails] = useState(
    {
      currency: "",
      companyKey: "",
      shippingMethod: "",
      deliveryInstructions: "",
      poNumber: "",
      shippingAddress: {
        firstName: "",
        lastName: "",
        streetNumber: "",
        streetName: "",
        city: "",
        postCode: "",
        region: "",
        country: "",
        phone: "",
        suburb: ""
      }
    })
  const locale = getCookieValue('locale');
  const { account } = useAccount();
  const [apiAddressWasEmpty, setApiAddressWasEmpty] = useState(false);
  const [errorMessage, setErrorMesage] = useState<any>({});
  const [loading, setLoading] = useState(false)
  const [validateForm, setValidateForm] = useState(false);
  // 1. Create a ref to track if we've shown the toast
  const { cart, updateCart, setShippingMethod, redeemDiscount, removeDiscount, shippingMethods } = useCart(
    "",
    locale === 'au' ? "plumbershub-au" : "plumbershub-nz"
  );
  const { isCartEnabled, isLoading } = useCartConfig();
  useEffect(() => {
    if (!isLoading && !isCartEnabled) {
      router.push(`/${locale}/`);
    }
  }, [isCartEnabled, isLoading, router, locale]);


  const currencyCode = getCurrencyByLocale(locale);

  const fetchCheckoutDetails = async () => {
    setLoading(true);
    try {
      const response = await Service.checkout.details({ currency: currencyCode, businessUnitKey: "" as string });
      if (response?.data) {
        const detailsData = response?.data;
        const shippingAddress = {
          firstName: detailsData?.shippingAddress?.firstName ?? '',
          lastName: detailsData?.shippingAddress?.lastName ?? '',
          streetNumber: detailsData?.shippingAddress?.streetNumber ?? '',
          streetName: detailsData?.shippingAddress?.streetName ?? '',
          city: detailsData?.shippingAddress?.city ?? '',
          postCode: detailsData?.shippingAddress?.postcode ?? '',
          region: detailsData?.shippingAddress?.state ?? '',
          country: detailsData?.shippingAddress?.country ?? '',
          phone: detailsData?.shippingAddress?.phone ?? '',
          suburb: detailsData?.shippingAddress?.suburb ?? '',
        };

        // Check if all key address fields are empty — will fall back to account profile
        const isEmpty = !shippingAddress.firstName &&
          !shippingAddress.lastName &&
          !shippingAddress.streetName &&
          !shippingAddress.postCode &&
          !shippingAddress.suburb;
        setApiAddressWasEmpty(isEmpty);

        let data: any = {
          currency: detailsData?.currency,
          companyKey: detailsData?.companyKey,
          shippingMethod: detailsData?.shippingMethodDetails?.shippingMethodKey,
          deliveryInstructions: detailsData?.deliveryInstructions,
          poNumber: detailsData?.poNumber,
          shippingAddress,
        };
        setDetails((prev) => ({
          ...prev,
          ...data,
        }));
      } else {
        console.log('failed to fetch checkout details:: error:');
      }
    } catch (e) {
      console.log('failed to fetch checkout:: error:', e);
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currencyCode) {
      fetchCheckoutDetails()
    }
  }, [currencyCode])

  // If checkout API returned no shipping address, populate from account profile
  useEffect(() => {
    if (!apiAddressWasEmpty || !account) return;

    // Match country against the countries array (case-insensitive)
    const matchedCountry = countries.find(
      c => c.value.toLowerCase() === (account.country ?? '').toLowerCase()
    );
    const country = matchedCountry?.value ?? '';

    // Match state against auStates or nzStates depending on country (case-insensitive)
    const stateOptions = country === 'NZ' ? nzStates : auStates;
    const matchedRegion = stateOptions.find(
      s => s.value.toLowerCase() === (account.state ?? '').toLowerCase()
    );
    const region = matchedRegion?.value ?? '';

    setDetails(prev => ({
      ...prev,
      shippingAddress: {
        ...prev.shippingAddress,
        firstName: account.firstName ?? '',
        lastName: account.lastName ?? '',
        phone: account.phone ?? '',
        streetName: account.street ?? '',
        suburb: account.suburb ?? '',
        postCode: account.postalCode ?? '',
        city: account.suburb ?? '',
        country,
        region,
      },
    }));
  }, [apiAddressWasEmpty, account]);

  const handleReview = async (e: any) => {
    e.preventDefault();
    setValidateForm(true);

    let hasError = false;
    let firstErrorMessage = '';

    const requiredFields = [
      'firstName',
      'lastName',
      'streetName',
      'city',
      'postCode',
      'country',
      'region',
      'phone',
    ];

    const hasEmptyFields = requiredFields.some(field => {
      const value = details?.shippingAddress?.[field as keyof typeof details.shippingAddress];
      return !value || value.trim() === '';
    });

    if (hasEmptyFields) {
      hasError = true;
      firstErrorMessage = 'Please fill in all required address fields';
    }

    const postCode = details?.shippingAddress?.postCode;
    if (postCode) {
      if (!/^\d+$/.test(postCode)) {
        hasError = true;
        if (!firstErrorMessage) firstErrorMessage = 'Postcode must contain only numbers';
      } else if (postCode.length < 4 || postCode.length > 5) {
        hasError = true;
        if (!firstErrorMessage) firstErrorMessage = 'Please enter a valid 4–5 digit postcode';
      }
    } else {
      hasError = true;
      if (!firstErrorMessage) firstErrorMessage = 'Please enter a valid postcode';
    }

    if (!details?.shippingMethod || details?.shippingMethod.trim() === '') {
      hasError = true;
      setErrorMesage({ shippingMethod: 'Please select one of the Delivery Options!' });
      if (!firstErrorMessage) firstErrorMessage = 'Please select one of the Delivery Options!';
    } else {
      setErrorMesage({ shippingMethod: '' });
    }

    if (hasError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error(firstErrorMessage, { position: 'top-right' });
      return;
    }

    setLoading(true);
    try {
      const data: ShippingAddressUpdatePayload = {
        currency: currencyCode,
        companyKey: '',
        shippingMethod: details?.shippingMethod,
        deliveryInstructions: details?.deliveryInstructions,
        poNumber: '',
        shippingAddress: {
          firstName: details?.shippingAddress?.firstName,
          lastName: details?.shippingAddress?.lastName,
          streetNumber: details?.shippingAddress?.streetNumber,
          streetName: details?.shippingAddress?.streetName,
          city: details?.shippingAddress?.city,
          postCode: details?.shippingAddress?.postCode,
          region: details?.shippingAddress?.region,
          country: details?.shippingAddress?.country,
          phone: details?.shippingAddress?.phone,
          suburb: details?.shippingAddress?.suburb,
        },
      };

      const response = await Service.checkout.updateShipmentdetails(data);

      if (response?.data) {
        toast.success('Updated Successfully!', {
          position: 'top-right',
          duration: 2000,
        });

        setTimeout(() => {
          router.push(`/${locale}/checkout/review`);
          fetchCheckoutDetails();
        }, 2000);
      } else {
        toast.error(
          "We're experiencing an issue. Please reach out using the details provided on this page.",
          { position: 'top-right' }
        );
      }
    } catch (e) {
      console.log('failed to save checkout:: error:', e);
      toast.error(
        "We're experiencing an issue. Please reach out using the details provided on this page.",
        { position: 'top-right' }
      );
    } finally {
      setLoading(false);
      setValidateForm(false);
    }
  };


  useEffect(() => {
    if (shippingMethods) {
      const method: any = shippingMethods?.find((_item: any) => _item?.name === details?.shippingMethod)
      if (method) {
        setDetails((prev: any) => {
          return {
            ...prev,
            shippingMethodKey: method?.key
          }
        })
      }
    }
  }, [shippingMethods, details?.shippingMethod])

  const filteredShippingMethods = shippingMethods?.filter((item: any) => item?.key.includes("plumbershub"))


  return (
    <Checkout
      {...cart}
      validateForm={validateForm}
      shippingMethods={filteredShippingMethods}
      details={details}
      setDetails={setDetails}
      setValidateForm={setValidateForm}
      loading={loading}
      initialData={{
        shippingAddress: cart?.shippingAddress && mapAddress(cart?.shippingAddress),
        billingAddress: cart?.billingAddress && mapAddress(cart?.billingAddress),
        shippingMethodId: cart?.shippingInfo?.shippingMethodId,
      }}
      handleReview={handleReview}
      error={errorMessage}
      setError={setErrorMesage}
      addresses={[]}
    />
  );
};

export default CheckoutTastic;
